import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useLocation } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "app/hooks";

import {
  type HeaderChoicesByScope,
  type HeaderPreferenceStatus,
  type UserHeaderChoice,
  hydrateUserHeaderChoices,
  selectHeaderAnimationInProgress,
  selectHeaderPreferenceStatus,
  selectIsHeaderCollapsed,
  selectUserHeaderChoices,
  setHeaderAnimationInProgress,
  setIsHeaderCollapsed,
  setUserHeaderChoice
} from "./patient-header-slice";
import {
  getHeaderScope,
  shouldPersistHeaderChoice
} from "./use-patient-header-auto-collapse.hook";
import usePatientHeaderCollapsePreference from "./use-patient-header-collapse-preference.hook";

/**
 * How long the collapse and expand animations run before the elements are swapped.
 *
 * These two disagree with each other AND with the CSS (0.25s on the collapsed bar, 0.35s on the
 * expanded header), and that is deliberately left alone. Unifying them — as the first version of
 * DEV-2533 did — silently regresses the six pages that collapse the header on mount and expand
 * it on cleanup (HCFA wizard, edit-sale V1/V2, both exchange-product variants, HA forms, the
 * new-HA wizard header).
 *
 * Why: on navigation React runs the child page's `collapseHeader()` first and
 * `patient-header-layout`'s `expandHeader("auto")` second (effects run child-before-parent).
 * Neither cancels the other, so the winner is whichever lands
 * LAST by wall clock — the 250ms collapse, over the 200ms expand. Those pages have always
 * depended on that 50ms gap. Making the timings equal (or cancelling the pending timer) flips it
 * to last-call-wins and every one of them loses its collapsed header, with nothing in CI to
 * catch it.
 *
 * Fixing that properly means making those pages' intent explicit — most likely by adding their
 * routes to `isRouteRequiringCollapsedHeader` — and belongs in its own ticket.
 */
const COLLAPSE_ANIMATION_MS = 250;
const EXPAND_ANIMATION_MS = 200;

/**
 * Who asked for the header to move.
 *
 * `"user"` — someone clicked the bar or the collapse button. Records a choice for the scope they
 * are currently on, persisted per user, which then outranks that scope's automatic default.
 * `"auto"` — the route effect, or a page that needs the space while it is mounted (the HCFA
 * wizard, edit-sale, exchange product...). Deliberately the default so all ~8 pre-existing
 * callers keep working unchanged and none of them can masquerade as user intent.
 */
export type HeaderToggleSource = "user" | "auto";

export type HeaderToggleOptions = {
  /**
   * `false` swaps the header immediately instead of running the CSS animation. Used for
   * `prefers-reduced-motion`, where the auto-collapse must not be the one animation the
   * user never asked for.
   */
  animate?: boolean;
};

const PatientHeaderContext = React.createContext<{
  isHeaderCollapsed: boolean | undefined;
  headerAnimationInProgress: string | undefined;
  userHeaderChoices: HeaderChoicesByScope;
  preferenceStatus: HeaderPreferenceStatus;
  collapseHeader: (
    source?: HeaderToggleSource,
    options?: HeaderToggleOptions
  ) => void;
  expandHeader: (
    source?: HeaderToggleSource,
    options?: HeaderToggleOptions
  ) => void;
  onNewTouchpointCloseModalCallback: () => void;
  setNewTouchpointCloseModalCallback: (callback?: () => void) => void;
} | null>(null);

type PatientHeaderProviderProps = {
  children?: React.ReactNode;
};

export function PatientHeaderProvider({
  children
}: PatientHeaderProviderProps) {
  const dispatch = useAppDispatch();

  const isHeaderCollapsed = useAppSelector(selectIsHeaderCollapsed);
  const headerAnimationInProgress = useAppSelector(
    selectHeaderAnimationInProgress
  );
  const userHeaderChoices = useAppSelector(selectUserHeaderChoices);
  const preferenceStatus = useAppSelector(selectHeaderPreferenceStatus);

  const { isStorageReady, readStoredChoices, writeStoredChoice } =
    usePatientHeaderCollapsePreference();

  // Which scope a click is about. Read through a ref, never a dependency: this provider wraps the
  // whole authenticated app, so depending on `pathname` would hand `collapseHeader`/`expandHeader`
  // a fresh identity on every navigation — and the ~7 pages that collapse the header on mount and
  // `return expandHeader` as cleanup hold both in their effect deps, so the pair would re-fire on
  // each route change.
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  useEffect(() => {
    dispatch(setIsHeaderCollapsed(false));
    dispatch(setHeaderAnimationInProgress(""));
  }, [dispatch]);

  // Seed both scopes from localStorage as soon as the user id resolves. Until this lands,
  // `preferenceStatus` is "loading" and `patient-header-layout` leaves the header alone — acting
  // on a not-yet-read preference would paint a header the user then watches disappear. See the
  // `HeaderPreferenceStatus` docstring in the slice.
  useEffect(() => {
    if (!isStorageReady) return;
    dispatch(hydrateUserHeaderChoices(readStoredChoices()));
  }, [isStorageReady, readStoredChoices, dispatch]);

  /**
   * Record a deliberate choice for the scope the user is on, in Redux and on disk.
   *
   * Every screen height, on purpose. This used to be short-viewport-only, which paired with a
   * resolver that returned "expanded" on a tall viewport before it ever read the value — so a
   * large-screen user could collapse the header and watch the very next navigation throw the
   * choice away. Suppressing an AUTOMATIC collapse on a large screen is still the rule
   * (`resolveHeaderState` step 3); discarding a deliberate click never was.
   *
   * The one route class that records nothing is the one whose state is dictated regardless — see
   * `shouldPersistHeaderChoice`. Without that guard, clicking "Show patient details" on an
   * audiology report would quietly un-collapse every other tab.
   */
  const persistHeaderChoice = useCallback(
    (choice: UserHeaderChoice) => {
      const pathname = pathnameRef.current;
      if (!shouldPersistHeaderChoice(pathname)) return;

      const scope = getHeaderScope(pathname);
      dispatch(setUserHeaderChoice({ scope, choice }));
      writeStoredChoice(scope, choice);
    },
    [dispatch, writeStoredChoice]
  );

  const collapseHeader = useCallback(
    (source: HeaderToggleSource = "auto", options?: HeaderToggleOptions) => {
      if (source === "user") persistHeaderChoice("collapsed");

      if (options?.animate === false) {
        dispatch(setHeaderAnimationInProgress(""));
        dispatch(setIsHeaderCollapsed(true));
        return;
      }

      dispatch(setHeaderAnimationInProgress("closing"));

      setTimeout(() => {
        dispatch(setIsHeaderCollapsed(true));
        dispatch(setHeaderAnimationInProgress(""));
      }, COLLAPSE_ANIMATION_MS);
    },
    [dispatch, persistHeaderChoice]
  );

  const expandHeader = useCallback(
    (source: HeaderToggleSource = "auto", options?: HeaderToggleOptions) => {
      if (source === "user") persistHeaderChoice("expanded");

      if (options?.animate === false) {
        dispatch(setHeaderAnimationInProgress(""));
        dispatch(setIsHeaderCollapsed(false));
        return;
      }

      dispatch(setHeaderAnimationInProgress("expanding"));

      setTimeout(() => {
        dispatch(setIsHeaderCollapsed(false));
        dispatch(setHeaderAnimationInProgress(""));
      }, EXPAND_ANIMATION_MS);
    },
    [dispatch, persistHeaderChoice]
  );

  const newTouchpointCloseModalCallbackRef = useRef<any>();

  const onNewTouchpointCloseModalCallback = useCallback(() => {
    if (newTouchpointCloseModalCallbackRef.current) {
      newTouchpointCloseModalCallbackRef.current();
    }
  }, []);

  const setNewTouchpointCloseModalCallback = useCallback(
    (callback?: () => void) => {
      newTouchpointCloseModalCallbackRef.current = callback;
    },
    []
  );

  const value = useMemo(
    () => ({
      isHeaderCollapsed,
      headerAnimationInProgress,
      userHeaderChoices,
      preferenceStatus,
      collapseHeader,
      expandHeader,
      onNewTouchpointCloseModalCallback,
      setNewTouchpointCloseModalCallback
    }),
    [
      isHeaderCollapsed,
      headerAnimationInProgress,
      userHeaderChoices,
      preferenceStatus,
      collapseHeader,
      expandHeader,
      onNewTouchpointCloseModalCallback,
      setNewTouchpointCloseModalCallback
    ]
  );

  return (
    <PatientHeaderContext.Provider value={value}>
      {children}
    </PatientHeaderContext.Provider>
  );
}

export default function usePatientHeader() {
  const context = React.useContext(PatientHeaderContext);
  if (!context) {
    throw new Error(
      "usePatientHeader must be used within a PatientHeaderProvider"
    );
  }

  const {
    isHeaderCollapsed,
    headerAnimationInProgress,
    userHeaderChoices,
    preferenceStatus,
    collapseHeader,
    expandHeader,
    onNewTouchpointCloseModalCallback,
    setNewTouchpointCloseModalCallback
  } = context;

  return {
    isHeaderCollapsed,
    headerAnimationInProgress,
    userHeaderChoices,
    preferenceStatus,
    collapseHeader,
    expandHeader,
    onNewTouchpointCloseModalCallback,
    setNewTouchpointCloseModalCallback
  };
}
